import React, { useEffect, useState } from "react";
import Navbar from "../navbar/navbar";
import DatePicker from "react-datepicker";
import { formatDate, formatISODate, getBase64ImageFromUrl, getSevaWiseExcelReport, getSevaWiseReportList } from "../../services/receipt.service";
import Datatable from "../../datatable/Datatable";
import { getPoojaMasterList } from "../../services/admin.service";
import Swal from "sweetalert2";
import Select from 'react-select';
import { SevaWiseReportPrint } from "../../print/sevaWiseReportPrint";
import headerLogo from "../../assets/images/DailyPoojaReport.png"
import "./reportModule.css";
import { ReportExportActions, ReportListHeading, ReportListLoading } from "./ReportListUi";

const SevaWiseReportComponent = () => {

    const [sevaWiseReportList, setSevaWiseReportList] = useState([]);
    const [poojaMasterList, setPoojaMasterList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [poojaLoading, setPoojaLoading] = useState(true);
    const [selectedPooja, setSelectedPooja] = useState(null);

    const [fromDate, setFromDate] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth(), 1); // 1st of current month
    });

    const [toDate, setToDate] = useState(() => {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth() + 1, 0); // Last day of current month
    });

    useEffect(() => {
        fetchPoojaList();
    }, []);

    useEffect(() => {
        if (poojaMasterList.length > 0 && !selectedPooja) {
            const firstPooja = {
                value: poojaMasterList[0].poojaId,
                label: poojaMasterList[0].poojaName
            };
            setSelectedPooja(firstPooja);
        }
    }, [poojaMasterList]);

    useEffect(() => {
        if (fromDate && toDate && selectedPooja?.value) {
            fetchSevaWiseReportList(fromDate, toDate, selectedPooja.value);
        }
    }, [fromDate, toDate, selectedPooja]);

    const fetchSevaWiseReportList = async (from, to, poojaId) => {
        setLoading(true);
        try {
            const fromDate = formatISODate(from);
            const toDate = formatISODate(to);
            const response = await getSevaWiseReportList(fromDate, toDate, poojaId);
            if (response) {
                setSevaWiseReportList(response);
            } else {
                Swal.fire("Error", "Failed to fetch seva wise report list", "error");
            }
        } catch (error) {
            console.error(error);
            Swal.fire("Error", "Something went wrong", "error");
        } finally {
            setLoading(false);
        }
    };

    const fetchPoojaList = async () => {
        setPoojaLoading(true);
        try {
            const response = await getPoojaMasterList();
            if (response) {
                setPoojaMasterList(response);
            } else {
                Swal.fire("Error", "Failed to fetch pooja list", "error");
                setPoojaMasterList([]);
            }
        } catch (error) {
            Swal.fire("Error", "Something went wrong while fetching pooja list", "error");
            console.error(error);
        } finally {
            setPoojaLoading(false);
        }
    };

    const columns = [
        { name: "SN", selector: (row) => row.sn, sortable: true, align: 'text-center' },
        { name: "Receipt No", selector: (row) => row.receiptNo, sortable: true, align: 'text-start' },
        { name: "Receipt Date", selector: (row) => row.receiptDate, sortable: true, align: 'text-start' },
        { name: "Name of Devotee", selector: (row) => row.devoteeName, sortable: true, align: 'text-start' },
        { name: "Mobile", selector: (row) => row.mobile, sortable: true, align: 'text-start' },
        { name: "Seva Name", selector: (row) => row.poojaName, sortable: true, align: 'text-start' },
        { name: "Seva Date", selector: (row) => row.poojaDateTime1, sortable: true, align: 'text-start' },
        { name: "Total Person", selector: (row) => row.totalPerson, sortable: true, align: 'text-start' },
        { name: "Total Amount", selector: (row) => row.totalAmount, sortable: true, align: 'text-start' },
    ];

    const mappedData = () => {
        return sevaWiseReportList.map((item, index) => ({
            sn: index + 1,
            receiptNo: item.receiptNo || '-',
            receiptDate: formatDate(item.receiptDate) || '-',
            devoteeName: item.devoteeName || '-',
            mobile: item.mobile || '-',
            poojaName: item.poojaName || '-',
            poojaDateTime1: formatDate(item.poojaDateTime1) || '-',
            totalPerson: item.totalPerson || '-',
            totalAmount: item.totalAmount || '-',
        }))
    }

    const grandTotal = sevaWiseReportList.reduce((sum, item) => sum + (item.totalAmount || 0), 0);

    const handlePrint = async () => {
        const base64Logo = await getBase64ImageFromUrl(headerLogo);
        await SevaWiseReportPrint(selectedPooja?.label, fromDate, toDate, base64Logo, sevaWiseReportList);
    };

    const handleDownload = async () => {
        const date1 = formatISODate(fromDate);
        const date2 = formatISODate(toDate);
        const response = await getSevaWiseExcelReport(date1,date2,selectedPooja?.value,selectedPooja?.label);
        const blob = new Blob([response.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "sevaWiseReport.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
    };

    return (
        <div className="card report-list-page">
            <Navbar />
            <ReportListHeading title="Seva Wise Report" />
            <div className="report-list-toolbar rowHeadercolor">
                <div className="report-list-filters col-md-10 d-flex align-items-center flex-wrap">
                    {/* Pooja Dropdown */}
                    <div className="d-flex align-items-center me-4 mb-2">
                        <label className="font-label me-2 mb-0">Pooja:</label>
                        <div className="text-start width-400px">
                            <Select
                                options={poojaMasterList.map(p => ({ value: p.poojaId, label: p.poojaName }))}
                                value={selectedPooja}
                                onChange={(data) => setSelectedPooja(data)}
                                placeholder="Select Pooja"
                                isSearchable
                            />
                        </div>
                    </div>

                    {/* From Date */}
                    <div className="d-flex align-items-center me-4 mb-2">
                        <label className="font-label me-2 mb-0">From:</label>
                        <DatePicker
                            selected={fromDate}
                            onChange={(date) => setFromDate(date)}
                            className="form-control"
                            placeholderText="From Date"
                            dateFormat="dd-MM-yyyy"
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                            onKeyDown={(event) => event.preventDefault()}
                        />
                    </div>

                    {/* To Date */}
                    <div className="d-flex align-items-center mb-2">
                        <label className="font-label me-2 mb-0">To:</label>
                        <DatePicker
                            selected={toDate}
                            onChange={(date) => setToDate(date)}
                            className="form-control"
                            placeholderText="To Date"
                            dateFormat="dd-MM-yyyy"
                            showYearDropdown
                            showMonthDropdown
                            dropdownMode="select"
                            onKeyDown={(event) => event.preventDefault()}
                        />
                    </div>
                </div>
                <ReportExportActions onPrint={handlePrint} onDownload={handleDownload} disabled={loading || poojaLoading || !selectedPooja} />
            </div>

            <div id="card-body customized-card" className="report-list-table">
                {poojaLoading || loading ? <ReportListLoading message={poojaLoading ? "Loading pooja options..." : "Loading seva report..."} /> : <Datatable columns={columns} data={mappedData()} />}
            </div>
            <div className="report-list-summary">
                <h5 className="fw-bold">Grand Total: ₹ {grandTotal.toFixed(2)}</h5>
            </div>
        </div>
    );
}

export default SevaWiseReportComponent;